import {
  DirectoryItem,
  DirectoryItemCreate,
  DirectoryItemType,
  DirectoryItemUpdate,
  Header
} from './types'
import { HTTPGatewayClient } from '@/services/http-client'

export type Pageable<T> = {
  content: T[]
  empty: boolean
  first: boolean
  last: boolean
  number: number
  numberOfElements: number
  size: number
  totalElements: number
  totalPages: number
}

const BASE_URL = 'file/v2/v2'

const headerKeyMap = {
  type: 'label.fileType',
  uploadDate: 'createdDate',
  fileSize: 'label.fileSize:numeric'
}

export async function fetchItemsForDirectory({
  directoryId,
  searchTerm,
  sortHeaders,
  page,
  size
}: {
  directoryId: string
  searchTerm?: string
  sortHeaders?: Header[]
  page: number
  size: number
}): Promise<Pageable<DirectoryItem<DirectoryItemType, object>>> {
  const sorts =
    sortHeaders
      ?.filter((header) => header.sortDirection)
      ?.map(
        (header) => `${headerKeyMap[header.key] ?? 'label.' + header.key},${header.sortDirection}`
      ) || []
  const response = await HTTPGatewayClient.get<Pageable<DirectoryItem<DirectoryItemType, object>>>(
    `${BASE_URL}/directory`,
    {
      params: {
        entityId: directoryId !== 'root' ? directoryId : undefined,
        'labels.name.icontains': searchTerm && searchTerm.trim() !== '' ? searchTerm : undefined,
        sort: ['label.type,asc', ...sorts],
        page,
        size
      },
      paramsSerializer: {
        indexes: null
      }
    }
  )
  return response.data
}

export async function postDirectoryItem(
  item: DirectoryItemCreate
): Promise<DirectoryItem<DirectoryItemType, object>> {
  if (item.directoryId && item.directoryId === 'root') {
    item.directoryId = undefined
  }
  const endpoint = `${BASE_URL}/${item.type}`
  if (item.type === 'file' && item['file']) {
    const file = item['file'] as File
    const json = JSON.stringify(item)
    const blob = new Blob([json], {
      type: 'application/json'
    })
    const formData = new FormData()
    formData.append('body', blob)
    formData.append('file', file)
    const response = await HTTPGatewayClient.post<DirectoryItem<DirectoryItemType, object>>(
      endpoint,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return response.data
  } else {
    const response = await HTTPGatewayClient.post<DirectoryItem<DirectoryItemType, object>>(
      endpoint,
      item
    )
    return response.data
  }
}

export async function deleteDirectoryItem(item: DirectoryItem): Promise<void> {
  await HTTPGatewayClient.delete(`${BASE_URL}/${item.type}/${item.id}`)
}

export async function updateDirectoryItem(
  item: DirectoryItem,
  update: DirectoryItemUpdate
): Promise<void> {
  await HTTPGatewayClient.patch(`${BASE_URL}/${item.type}/${item.id}`, update)
}

export async function getDirectoryItemBlob(item: DirectoryItem<'file'>): Promise<Blob | null> {
  try {
    const response = await HTTPGatewayClient.get<Blob>(`${BASE_URL}/file/${item.id}/blob`, {
      responseType: 'blob'
    })
    return response.data
  } catch (error) {
    console.error('Error downloading file:', error)
    return null
  }
}

function downloadBlob(blob: Blob, filename: string): void {
  const link: HTMLAnchorElement = document.createElement('a')
  const url: string = URL.createObjectURL(blob)
  link.href = url
  link.download = filename

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export async function downloadDirectoryItemBlob(
  item: DirectoryItem<'file'>
): Promise<{ blob: Blob; fileName: string } | null> {
  const blob = await getDirectoryItemBlob(item)
  if (blob) {
    const fileName = item.name || `download-${item.id}.bin`
    downloadBlob(blob, fileName)
  }
  return null
}

export async function replaceDirectoryItem(
  item: DirectoryItemCreate
): Promise<DirectoryItem<DirectoryItemType, object>> {
  if (item.directoryId && item.directoryId === 'root') {
    item.directoryId = undefined
  }
  const endpoint = `${BASE_URL}/${item.type}`
  if (item.type === 'file' && item['file']) {
    const file = item['file'] as File
    const json = JSON.stringify(item)
    const blob = new Blob([json], {
      type: 'application/json'
    })
    const formData = new FormData()
    formData.append('body', blob)
    formData.append('file', file)
    const response = await HTTPGatewayClient.put<DirectoryItem<DirectoryItemType, object>>(
      endpoint,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return response.data
  } else {
    const response = await HTTPGatewayClient.put<DirectoryItem<DirectoryItemType, object>>(
      endpoint,
      item
    )
    return response.data
  }
}
